import React from 'react';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveSunburst } from "@nivo/sunburst";
import {generateGradientsAndColors, generateGradients, NivoChartLabel, NivoTextTheme, applyGradients} from "./utils";


const CustomLabel = ({ slice, label, value }) => {
    console.log(value);
  const angle = (slice.startAngle + slice.endAngle) / 2;
  const radius = slice.outerRadius + 30; // Adjust this value to position labels further out or closer in
  const x = radius * Math.cos(angle - Math.PI / 2);
  const y = radius * Math.sin(angle - Math.PI / 2);

  let rotation = (angle * 180) / Math.PI;
  if (rotation > 90 && rotation < 270) {
    rotation += 180;
  }

  const anchor = (rotation > 90 && rotation < 270) ? 'end' : 'start';

  return (
    <g transform={`translate(${x},${y}) rotate(${rotation})`}>
      <text
        textAnchor={anchor}
        dominantBaseline="central"
        style={{
          fontSize: '12px',
          fill: slice.color,
        }}
      >
        {`${label}: ${value}%`}
      </text>
    </g>
  );
};


/**
 *
 * @param data {Array[object]}
 *              Example:
 *                  [
 *                      {id: string, value: number},
 *                      {...},
 *                      ...
 *                  ]
 * @param customColors
 * @param precision
 * @param prefix
 * @param postfix
 * @param radius
 * @param legend
 * @param legendPosition
 * @param legendDirection
 * @param legendWidth
 * @param showLabels
 * @param size
 * @returns {JSX.Element}
 * @constructor
 */
const NivoPieChart = ({
    data,
    customColors,
    precision = 1,
    prefix='',
    postfix = '%',
    radius = 0.7,
    legend=false,
    legendPosition = 'bottom',
    legendDirection = 'row',
    legendWidth=35,
    showLabels = true,
    size = null  // new prop for size with default as null
}) => {
    // Assume generateGradientsAndColors generates both gradients and a mapping of base colors for each key
    const { gradients, colorMapping } = generateGradientsAndColors(data.map(d => d.id), customColors);

    // Modify the data to include base color for each slice for use in legends and label lines
    const pieChartData = data.map(item => ({
        ...item,
        color: colorMapping[item.id] // Assuming colorMapping maps id to its base color
    }));
    const isTiny = size === 'tiny';
    const keys = data.map(x=>x.id);
    const baseColorMapping = d => colorMapping[d.id];

    const commonProperties = {
        margin: isTiny ? { top: 10, right: 10, bottom: 10, left: 10 } : {
            top: legendPosition === 'top' || !legend ? 0 : legendWidth,
            right: legendPosition === 'right' || !legend ? 0 : legendWidth,
            bottom: legendPosition === 'bottom' || !legend ? 0 : legendWidth,
            left: legendPosition === 'left' || !legend ? 0 : legendWidth,
        },
        innerRadius: isTiny ? 0 : radius,
        padAngle: isTiny ? 0 : radius,
        cornerRadius: isTiny ? 1 : 3,
        borderWidth: 1,
        borderColor: { from: 'color', modifiers: [['darker', 0.2]] },
        colors: baseColorMapping,
        defs: generateGradients(keys, colorMapping),
        fill: applyGradients(keys),
        animate: true,
        motionConfig: "gentle"
    };
    if (showLabels && !isTiny) {
        commonProperties.margin = {top:30, bottom: 30, left: 40, right: 30};
    }

    const pieProps = isTiny ? {
        enableArcLinkLabels: false,
        enableArcLabels: false,
        legends: []
    } : {
        enableArcLinkLabels: showLabels,
        arcLabelsTextColor: "#ffffff", // Set this to white
        arcLinkLabelsColor: baseColorMapping,
        arcLinkLabelsThickness: 0,
        arcLinkLabelsSkipAngle: 12,
        arcLinkLabelsTextColor: '#c4bfbf',

        legends: [
            {
                anchor: legendPosition,
                direction: legendDirection,
                justify: false,
                translateX: 0,
                translateY: legendPosition === 'bottom' ? 56 : 0,
                itemsSpacing: 0,
                itemWidth: legendWidth,
                itemHeight: 30,
                itemTextColor: '#999',
                itemDirection: 'left-to-right',
                itemOpacity: 1,
                symbolSize: 18,
                symbolShape: 'circle',
                effects: [
                    {
                        on: 'hover',
                        style: {
                            itemTextColor: '#000'
                        }
                    }
                ],
                data: pieChartData.map(slice => ({
                    id: slice.id,
                    label: slice.id,
                    color: baseColorMapping(slice)
                }))
            }
        ]
    };

    return (
        <ResponsivePie
            data={pieChartData}
            theme={{ ...NivoTextTheme }}
            {...commonProperties}
            {...pieProps}
        />
    );
};

export default NivoPieChart;
